import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { MOCK_API } from 'src/config-global';

// Dedicated axios instance for the Course Creator Mock API
const courseAxios = axios.create({
    baseURL: MOCK_API,
});

const initialState = {
    isLoading: false,
    error: null,
    courses: [],
    currentCourse: null,
    shopTheme: null, // global appearance per shop (from mock server)
    enrollments: [], // student-course links
};

const slice = createSlice({
    name: 'course-creator',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        setCourses(state, action) {
            state.isLoading = false;
            state.courses = action.payload;
        },
        setCurrentCourse(state, action) {
            state.currentCourse = action.payload;
        },
        setShopTheme(state, action) {
            state.shopTheme = action.payload;
            state.isLoading = false;
        },
        setEnrollments(state, action) {
            state.enrollments = action.payload || [];
            state.isLoading = false;
        },
    },
});

export default slice.reducer;
export const { actions } = slice;

export function getCoursesByShop(shopId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            // Fetch courses filtered by shopId (supported by json-server)
            // We use the 'shopId' query parameter to filter results on the server side
            const response = await courseAxios.get(`/courses?shopId=${shopId}`);

            dispatch(slice.actions.setCourses(response.data));
        } catch (error) {
            console.warn("Mock Server not running? Fallback to empty.", error);
            // Don't set error to avoid blocking UI, just stop loading
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function getCourseTheme(shopId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await courseAxios.get(`/courseThemeSettings?shopId=${shopId}`);
            const theme = Array.isArray(response.data) ? response.data[0] : null;
            dispatch(slice.actions.setShopTheme(theme || null));
        } catch (error) {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function getEnrollments(shopId, studentId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await courseAxios.get(`/studentCourses?shopId=${shopId}&studentId=${studentId}`);
            dispatch(slice.actions.setEnrollments(response.data || []));
        } catch (error) {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function createCourse(newCourse) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await courseAxios.post('/courses', newCourse);
            // After creating, we could re-fetch or just append locally if we had an append reducer
            // For now, let's just stop loading.
            dispatch(slice.actions.stopLoading());
            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function updateCourse(id, updatedCourse) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await courseAxios.put(`/courses/${id}`, updatedCourse);
            dispatch(slice.actions.stopLoading());
            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function deleteCourse(id) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            await courseAxios.delete(`/courses/${id}`);
            dispatch(slice.actions.stopLoading());
            return true;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function getCourse(id) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await courseAxios.get(`/courses/${id}`);
            dispatch(slice.actions.setCurrentCourse(response.data));
            return response.data;
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}
